import * as React from 'react';

import { cn } from '@/ux-common/lib/utils';

/**
 * Icon - Minimal React wrapper for the decorated icon chrome.
 *
 * All visual styling is applied via CSS classes. The wrapper provides:
 * - `data-component="Icon"` for component identification
 * - Ref forwarding for tooltip/popover compatibility
 * - `className` merging via `cn()`
 *
 * The `.icon` base class is applied automatically (defaults: transparent variant,
 * sm size = 24px, round shape). Compose modifiers via className:
 * `.icon-{variant}`, `.icon-{sm|lg}`, `.icon-{round|square}`, `.icon-{overlap|pulse}`.
 *
 * The `children` slot is the glyph — a Lucide SVG, inline SVG, or `<img>` with
 * `className="glyph glyph-{sm|md|lg}"` applied directly. Pair `icon-sm` with
 * `glyph-sm` (16px inside 24px chrome) and `icon-lg` with `glyph-lg` (24px inside
 * 36px chrome). `glyph-md` (20px) is standalone-only — no Icon chrome wraps it.
 *
 * @example
 * ```tsx
 * // Default Icon (transparent + sm + round are baked into .icon)
 * <Icon><AlertCircle className="glyph glyph-sm" aria-hidden="true" /></Icon>
 *
 * // Critical variant with pulse
 * <Icon className="icon-critical icon-pulse" aria-label="Active alarm">
 *   <AlertCircle className="glyph glyph-sm" aria-hidden="true" />
 * </Icon>
 *
 * // Brand square, large
 * <Icon className="icon-brand icon-square icon-lg">
 *   <Sparkle className="glyph glyph-lg" aria-hidden="true" />
 * </Icon>
 *
 * // Tooltip composition (single ref-forwarded span)
 * const IconWithTooltip = withTooltip(Icon);
 * <IconWithTooltip className="icon-warning" tooltip="Threshold exceeded">
 *   <Triangle className="glyph glyph-sm" aria-hidden="true" />
 * </IconWithTooltip>
 * ```
 */

export type IconProps = React.HTMLAttributes<HTMLSpanElement>;

export const Icon = React.forwardRef<HTMLSpanElement, IconProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <span ref={ref} data-component="Icon" className={cn('icon', className)} {...props}>
        {children}
      </span>
    );
  }
);

Icon.displayName = 'Icon';
